import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Password from '../../../../components/Password';
import Button from '../../../../components/Button';
import Notification from '../../../../components/Notification/Notification';

import ServiceRegistration from '../../../../services/RegisterService';

import {
  gtmAttributesError,
  gtmAttributesRecoverPassword,
  gtmAttributesUpdatePasswordPage,
} from '../../../../utils/gtmVariables';
import useGTMEventsTracker from '../../../../hooks/useGTMEventsTracker';
import Typography from '../../../../components/Typography';

interface Props {
  next: Function;
}

const PasswordStep = ({ next }: Props) => {
  const [showWarning, setShowWarning] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showErrorConfirmation, setShowErrorConfirmation] = useState(false);
  const [password, setPassword] = useState<string | undefined>('');
  const [disableButton, setDisableButton] = useState(false);
  const [message, setMessage] = useState(<></>);
  const time = 5000;
  const navigate = useNavigate();
  const serviceRegistration = new ServiceRegistration();
  const param = useParams();
  const token = param.token?.toString();
  const gtmEventsTracker = useGTMEventsTracker();

  gtmEventsTracker(gtmAttributesUpdatePasswordPage);

  const change = () => {
    setDisableButton(true);
    if (password && showConfirmation && token) {
      serviceRegistration
        .updatePassword(password, token)
        .then(() => {
          gtmEventsTracker(gtmAttributesRecoverPassword);
          setShowWarning(true);
          return next(2);
        })
        .catch((error) => {
          setDisableButton(false);
          if (error.response.status === 400) {
            const errorMessage = error.response.data.motivos
              ? error.response.data.motivos[0]
              : 'A senha deve respeitar todos os critérios.';
            setMessage(<>{errorMessage}</>);
            setShowError(true);
            setTimeout(() => {
              setShowErrorConfirmation(true);
            }, 400);
          } else if (error.response.data.mensagem.includes('JWT expired at')) {
            setMessage(
              <>
                O token de redefinição de senha já expirou, faça o pedido de um novo email de reset
                de senha.
              </>
            );
            setShowError(true);
            setTimeout(() => {
              setShowErrorConfirmation(true);
            }, 4000);
          }
          gtmEventsTracker(gtmAttributesError);
        });
    }
    setDisableButton(false);
  };
  const changePassword = (e: any) => {
    setShowConfirmation(e?.isValid);
    if (e.target) {
      setPassword(e.target.value);
    }
  };

  return (
    <>
      <div className="relative hidden sm:block">
        {showErrorConfirmation ? (
          <div className="absolute w-[400px] rounded-[9px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-200 p-5 z-[1000]">
            <Typography as="p" variant="body2">
              O token de redefinição de senha expirou. Você gostaria de solicitar um novo?
            </Typography>
            <div className="flex justify-between">
              <Button
                type="button"
                text="Sim"
                variant="primary"
                classNames="w-2/5"
                onClick={() => navigate('/forgot-password')}
              />
              <Button
                type="button"
                text="Não"
                variant="secondary"
                classNames="w-2/5"
                onClick={() => setShowErrorConfirmation(false)}
              />
            </div>
          </div>
        ) : (
          <></>
        )}
        {showError ? (
          <Notification
            delay={time}
            variant="error"
            extraClass={
              'absolute sm:top-3 right-[8%] max-[640px]:left-0 max-[640px]:right-0 max-[640px]:top-1'
            }
          >
            <Typography as="p" variant="body2">
              {message}
            </Typography>
          </Notification>
        ) : (
          <></>
        )}
      </div>
      <div className="mx-auto flex flex-col items-center px-5 py-12">
        <div className="block sm:hidden">
          {showWarning ? (
            <Notification delay={time} variant="warning" extraClass="absolute sm:top-3 right-[8%]">
              Aguarde enquanto processamos suas informações.
            </Notification>
          ) : (
            <></>
          )}
          {showError ? (
            <Notification delay={time} variant="error" extraClass="absolute sm:top-3 right-[8%]">
              A senha precisa respeitar todas as regras!
            </Notification>
          ) : (
            <></>
          )}
        </div>
        <Typography as="span" variant="title1" className="text-center mb-2">
          Cadastre sua nova senha
        </Typography>
        <Typography as="p" variant="body2" className="text-center mb-12">
          No campo abaixo insira a sua nova senha.
        </Typography>
        <div className="mb-6 w-full md:max-w-[470px]">
          <Password label="Nova senha" onChange={(e: any) => changePassword(e)} />
        </div>
        <div className="flex flex-col md:flex-row-reverse w-full md:max-w-[470px] mt-6 mb-28 gap-3">
          <Button
            id="mp__btn-password_keep"
            text="Continuar"
            type="submit"
            variant="primary"
            classNames="mp__forgot_password-action_button rounded-lg w-full"
            onClick={change}
            disabled={disableButton}
          />
          <Button
            id="mp__btn-password_cancel"
            text="Cancelar"
            type="submit"
            variant="secondary"
            classNames="mp__forgot_password-action_button rounded-lg w-full"
            onClick={() => navigate('/login')}
          />
        </div>
      </div>
    </>
  );
};

export default PasswordStep;
